/**
 * The single HTTP boundary between this application and the backend.
 *
 * Every request in the app goes through `request()`; no component calls `fetch` itself. That
 * keeps four things in one place: the bearer session, the correlation id, the error
 * normalisation (src/api/errors.ts) and the 401 hook that drives the session-expired state.
 *
 * The client never sends anything derived from a claim token, and never logs a request body.
 */
import { type ApiError, apiErrorFromEnvelope, networkError } from './errors';

export interface ApiClientConfig {
  /** Backend origin, e.g. https://hpw-credentials.onrender.com */
  baseUrl: string;
  /** Resolves the current access token, or null when signed out. */
  getAccessToken: () => Promise<string | null> | string | null;
  /** Called once per request that comes back 401, so the shell can show "session expired". */
  onUnauthenticated?: (error: ApiError) => void;
  /** Overridable for tests. */
  fetchImpl?: typeof fetch;
  /** Request timeout in ms (0 disables). */
  timeoutMs?: number;
}

export type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface RequestOptions {
  method?: Method;
  /** JSON body. Mutually exclusive with `binary`. */
  body?: unknown;
  /** Raw bytes for the binary upload routes (assets, fonts, batch files). */
  binary?: { bytes: BodyInit; contentType: string };
  query?: Record<string, string | number | boolean | null | undefined>;
  /** Value for the `Idempotency-Key` header (required by POST /credentials). */
  idempotencyKey?: string;
  /** `corr_<12 hex>`; the backend ignores anything else and generates its own. */
  correlationId?: string;
  /** Skip the Authorization header (public and claim routes). */
  anonymous?: boolean;
  signal?: AbortSignal;
}

/** Correlation ids the backend accepts: `corr_` + 12 lowercase hex (BACKEND §10). */
export function newCorrelationId(): string {
  const bytes = new Uint8Array(6);
  crypto.getRandomValues(bytes);
  let hex = '';
  for (const b of bytes) hex += b.toString(16).padStart(2, '0');
  return `corr_${hex}`;
}

export function isCorrelationId(value: string): boolean {
  return /^corr_[0-9a-f]{12}$/.test(value);
}

/**
 * A key for ONE user intent, kept for as many attempts as that intent takes.
 *
 * The backend accepts any non-blank, whitespace-free string up to its length cap and answers a
 * repeat of the same key with the record it already made (`idempotentHit`). That guarantee only
 * does anything if the key is stable across a retry, so it is minted where the intent begins — when
 * the dialog opens — and never per request.
 */
export function newIdempotencyKey(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  let hex = '';
  for (const b of bytes) hex += b.toString(16).padStart(2, '0');
  return `idem_${hex}`;
}

function buildUrl(baseUrl: string, path: string, query: RequestOptions['query']): string {
  const url = new URL(path.startsWith('/') ? path.slice(1) : path, ensureTrailingSlash(baseUrl));
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

function ensureTrailingSlash(base: string): string {
  return base.endsWith('/') ? base : `${base}/`;
}

export class ApiClient {
  private readonly config: ApiClientConfig;
  /*
   * Shared with every correlated view of this client rather than copied. `withCorrelation` builds a
   * second instance so one click's requests can carry one id, and with a set of its own that second
   * instance swallowed every 403 the product actually makes: the write commands all go through it.
   */
  private forbidden = new Set<(error: ApiError) => void>();
  /* The same arrangement for 401, and for the same two reasons. */
  private unauthenticated = new Set<(error: ApiError) => void>();

  constructor(config: ApiClientConfig) {
    this.config = config;
  }

  /**
   * Learn when the server refuses this client's requests with 403.
   *
   * The role the screens use to decide what to OFFER is resolved once, at sign-in, from `/me`. An
   * owner working in a second window can change that role or disable the account underneath an open
   * session, and the backend applies the change on the very next request — but the open tab goes on
   * drawing the controls the role it remembers would allow. Nothing unauthorized happens, because
   * every command is authorized server-side; what happens is only bad to read: a surface that keeps
   * inviting somebody to do what the server will now refuse.
   *
   * A 403 is the server saying, unambiguously, that the remembered role is no longer the real one,
   * so the shell subscribes here and answers by re-reading the actor rather than by guessing a new
   * role locally. It is a subscription rather than a constructor option on purpose: the shell has to
   * be able to attach it to a client it was HANDED as well as to one it built, and a test that
   * injects its own client must exercise the same path the product runs.
   *
   * Returns the unsubscribe.
   */
  onForbidden(listener: (error: ApiError) => void): () => void {
    this.forbidden.add(listener);
    return () => {
      this.forbidden.delete(listener);
    };
  }

  /**
   * The same subscription for 401, for the same reason spelled out above.
   *
   * It existed only as a constructor option, which meant the shell could wire it to a client it
   * BUILT and not to one it was handed — so every test that injects its own client ran with the
   * product's 401 handling absent, and «a 401 ends the session» was never once exercised. That
   * matters more than it sounds: `resolveActor` answers 401 both for a token it cannot verify and
   * for a good token whose subject has no internal user, and telling those two apart is the
   * difference between asking somebody to sign in again and destroying the AAL2 session the
   * first-owner command needs.
   *
   * Returns the unsubscribe.
   */
  onUnauthenticated(listener: (error: ApiError) => void): () => void {
    this.unauthenticated.add(listener);
    return () => {
      this.unauthenticated.delete(listener);
    };
  }

  get baseUrl(): string {
    return this.config.baseUrl;
  }

  /**
   * A view of this client that stamps every request with one correlation id.
   *
   * One click can be several requests — a reissue is one call, a recipient-address correction plus
   * a resend is two — and a correlation id is what ties them to each other and to the audit rows
   * the backend wrote. Without it the server mints a fresh id per request and the click cannot be
   * followed afterwards, which is exactly what §11 asks a button to be able to prove.
   *
   * An explicit `correlationId` on a single call still wins; nothing else about the client changes,
   * and the id is not a secret (it is `corr_` + 12 hex and carries no user data).
   */
  withCorrelation(correlationId: string): ApiClient {
    if (!isCorrelationId(correlationId)) return this;
    const wrapped = new ApiClient(this.config);
    /* The same listener sets, by reference: a 401 or 403 through this view is still this
       client's. */
    wrapped.forbidden = this.forbidden;
    wrapped.unauthenticated = this.unauthenticated;
    const original = wrapped.raw.bind(wrapped);
    wrapped.raw = (path, options = {}) => original(path, { correlationId, ...options });
    return wrapped;
  }

  /** JSON request. Throws `ApiError` for every non-2xx answer and for transport failures. */
  async json<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const response = await this.raw(path, options);
    if (response.status === 204) return undefined as T;
    const text = await response.text();
    if (!text) return undefined as T;
    try {
      return JSON.parse(text) as T;
    } catch {
      throw apiErrorFromEnvelope(response.status, {
        error: { code: 'malformed_response', message: 'response was not valid JSON' },
      });
    }
  }

  /** Binary/text download (artifacts, CSV reports). Returns the blob and its filename hint. */
  async download(
    path: string,
    options: RequestOptions = {},
  ): Promise<{ blob: Blob; filename: string | null; contentType: string | null }> {
    const response = await this.raw(path, options);
    const disposition = response.headers.get('content-disposition');
    const match = disposition ? /filename="?([^";]+)"?/i.exec(disposition) : null;
    return {
      blob: await response.blob(),
      filename: match?.[1] ?? null,
      contentType: response.headers.get('content-type'),
    };
  }

  /** Underlying request: resolves only for 2xx, throws a normalised ApiError otherwise. */
  async raw(path: string, options: RequestOptions = {}): Promise<Response> {
    const {
      method = 'GET',
      body,
      binary,
      query,
      idempotencyKey,
      correlationId,
      anonymous = false,
      signal,
    } = options;

    const headers = new Headers();
    headers.set('Accept', 'application/json');
    if (!anonymous) {
      const token = await this.config.getAccessToken();
      if (token) headers.set('Authorization', `Bearer ${token}`);
    }
    if (idempotencyKey !== undefined) headers.set('Idempotency-Key', idempotencyKey);
    if (correlationId && isCorrelationId(correlationId)) headers.set('X-Correlation-Id', correlationId);

    let payload: BodyInit | undefined;
    if (binary) {
      payload = binary.bytes;
      headers.set('Content-Type', binary.contentType);
    } else if (body !== undefined) {
      payload = JSON.stringify(body);
      headers.set('Content-Type', 'application/json');
    }

    const timeoutMs = this.config.timeoutMs ?? 30_000;
    const controller = new AbortController();
    const abortListener = () => controller.abort(signal?.reason);
    if (signal) {
      if (signal.aborted) controller.abort(signal.reason);
      else signal.addEventListener('abort', abortListener, { once: true });
    }
    const timer =
      timeoutMs > 0
        ? setTimeout(() => controller.abort(new Error('request timed out')), timeoutMs)
        : undefined;

    const doFetch = this.config.fetchImpl ?? fetch;
    let response: Response;
    try {
      response = await doFetch(buildUrl(this.config.baseUrl, path, query), {
        method,
        headers,
        // credentials stay 'omit': the session is a bearer token, never a cookie, so no
        // cross-origin cookie is ever sent and CSRF has no surface here
        credentials: 'omit',
        mode: 'cors',
        cache: 'no-store',
        redirect: 'follow',
        ...(payload === undefined ? {} : { body: payload }),
        signal: controller.signal,
      });
    } catch (cause) {
      throw networkError(cause);
    } finally {
      if (timer) clearTimeout(timer);
      if (signal) signal.removeEventListener('abort', abortListener);
    }

    if (response.ok) return response;

    let parsed: unknown = null;
    try {
      const text = await response.text();
      parsed = text ? JSON.parse(text) : null;
    } catch {
      parsed = null;
    }
    const error = apiErrorFromEnvelope(response.status, parsed);
    if (error.kind === 'unauthenticated') {
      this.config.onUnauthenticated?.(error);
      for (const listener of this.unauthenticated) listener(error);
    }
    if (error.kind === 'unauthorized') for (const listener of this.forbidden) listener(error);
    throw error;
  }
}

/** Reads the public-safe runtime configuration compiled into the bundle. */
export function readEnvConfig(): {
  apiBaseUrl: string;
  supabaseUrl: string;
  supabaseAnonKey: string;
  publicBaseUrl: string;
} {
  const env = import.meta.env;
  return {
    apiBaseUrl: (env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '') || window.location.origin,
    supabaseUrl: (env.VITE_SUPABASE_URL ?? '').replace(/\/+$/, ''),
    supabaseAnonKey: env.VITE_SUPABASE_ANON_KEY ?? '',
    publicBaseUrl: (env.VITE_PUBLIC_BASE_URL ?? '').replace(/\/+$/, '') || window.location.origin,
  };
}
